import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';

import { AppComponent } from './app.component';
import { PalettesListComponent } from './palettes-list/palettes-list.component';
import { PatternPalettesListComponent } from './pattern-palettes-list/pattern-palettes-list.component';
import { DelayedChangeDirective } from './directives/delayed-change.directive';
import { BeadCanvasComponent } from './bead-canvas/bead-canvas.component';
import { ToolbarComponent } from './toolbar/toolbar.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatGridListModule } from '@angular/material/grid-list';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatBadgeModule } from '@angular/material/badge';
import { HttpService } from './services/http-service';
import { PosthogService } from './services/posthog.service';
import { CookieConsentService } from './services/cookie-consent.service';
import { GoogleAuthService } from './services/google-auth.service';
import { CookieConsentBannerComponent } from './components/cookie-consent-banner/cookie-consent-banner.component';
import { MatDialogModule } from '@angular/material/dialog';
import { ReplaceBeadModal } from './modals/replace-bead-modal/replace-bead-modal';
import { TransferImageModal } from './modals/transfer-image-modal/transfer-image-modal';
import { PaletteEditorModal } from './modals/palette-editor-modal/palette-editor-modal';
import { PrintPreviewModal } from './modals/print-preview-modal/print-preview-modal';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { PatternExplorerModal } from './modals/pattern-explorer-modal/pattern-explorer-modal';
import { MatTreeModule } from '@angular/material/tree';
import { AddEditPatternModal } from './modals/add-edit-pattern-modal/add-edit-pattern-modal';
import { PromptModal } from './modals/prompt-modal/prompt-modal';
import { ConfirmModal } from './modals/confirm-modal/confirm-modal';
import { MatExpansionModule } from '@angular/material/expansion';
import { ProfileModal } from './modals/profile-modal/profile-modal';
import { TransformPatternModal } from './modals/transform-pattern-modal/transform-pattern-modal';
import { LoginHomeComponent } from './login-home/login-home.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { SubscriptionsModal } from './modals/subscriptions-modal/subscriptions-modal';
import { ChangePlanDoneModal } from './modals/change-plan-done-modal/change-plan-done-modal';
import { HomeComponent } from './home/home.component';
import { AppGlobalErrorhandler } from './helpers/app-global-error-handler';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NotificationComponent } from './components/notification/notification.component';
import { LocalStorageKeys } from './helpers/constants';
import { TimeoutInterceptor } from './interceptors/timeout.interceptor';
import { WelcomeOnboardingModalComponent } from './modals/welcome-onboarding-modal/welcome-onboarding-modal.component';
import { VideoPlayerModalComponent } from './modals/video-player-modal/video-player-modal.component';
import { PositionImageGuideModal } from './modals/position-image-guide-modal/position-image-guide-modal';
import { OnboardingChecklistComponent } from './components/onboarding-checklist/onboarding-checklist.component';
import { OnboardingHintDirective } from './directives/onboarding-hint.directive';
import { BeadPreviewDirective } from './directives/bead-preview.directive';
import { NewPatternSetupModalComponent } from './modals/new-pattern-setup-modal/new-pattern-setup-modal.component';
import { InsertRowsColsModal } from './modals/insert-rows-cols-modal/insert-rows-cols-modal';
import { PatternReaderModal } from './modals/pattern-reader-modal/pattern-reader-modal';
import { BeadListItemComponent } from './bead-list-item/bead-list-item.component';
import { ReaderSettingsModal } from './modals/reader-settings-modal/reader-settings-modal';
import { UpgradePreviewCtaComponent } from './components/upgrade-preview-cta/upgrade-preview-cta.component';

const routes = [
  {
    path: '',
    component: HomeComponent,
    pathMatch: 'full',
  },
  { path: 'login', component: LoginHomeComponent },
];

@NgModule({
  declarations: [
    AppComponent,
    LoginHomeComponent,
    HomeComponent,
    PalettesListComponent,
    PatternPalettesListComponent,
    DelayedChangeDirective,
    OnboardingHintDirective,
    BeadPreviewDirective,
    ToolbarComponent,
    BeadCanvasComponent,
    NotificationComponent,
    OnboardingChecklistComponent,
    CookieConsentBannerComponent,
    WelcomeOnboardingModalComponent,
    VideoPlayerModalComponent,
    PositionImageGuideModal,
    ReplaceBeadModal,
    PatternExplorerModal,
    AddEditPatternModal,
    PromptModal,
    ConfirmModal,
    TransferImageModal,
    TransformPatternModal,
    PaletteEditorModal,
    PrintPreviewModal,
    ProfileModal,
    SubscriptionsModal,
    ChangePlanDoneModal,
    NewPatternSetupModalComponent,
    InsertRowsColsModal,
    PatternReaderModal,
    BeadListItemComponent,
    ReaderSettingsModal,
    UpgradePreviewCtaComponent,
  ],
  entryComponents: [
    WelcomeOnboardingModalComponent,
    VideoPlayerModalComponent,
    PositionImageGuideModal,
    ReplaceBeadModal,
    PatternExplorerModal,
    AddEditPatternModal,
    PromptModal,
    ConfirmModal,
    TransferImageModal,
    TransformPatternModal,
    PaletteEditorModal,
    PrintPreviewModal,
    ProfileModal,
    SubscriptionsModal,
    ChangePlanDoneModal,
    NewPatternSetupModalComponent,
    InsertRowsColsModal,
    PatternReaderModal,
    ReaderSettingsModal,
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    HttpClientModule,
    FormsModule,
    RouterModule.forRoot(routes),
    BrowserAnimationsModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatTooltipModule,
    MatDividerModule,
    MatMenuModule,
    MatTabsModule,
    MatSidenavModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatCardModule,
    MatListModule,
    MatGridListModule,
    FlexLayoutModule,
    MatButtonToggleModule,
    MatRadioModule,
    MatBadgeModule,
    MatDialogModule,
    MatTableModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    DragDropModule,
    MatTreeModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatSliderModule,
  ],
  providers: [
    HttpService,
    PosthogService,
    CookieConsentService,
    GoogleAuthService,
    {
      provide: ErrorHandler,
      useClass: AppGlobalErrorhandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimeoutInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
